import TC from "@/components/table/Table.module.css";

import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { VatText } from "@/components/VatText.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { CurrencyBalance, CurrencyHourly } from "@/components/l10n/Currency.tsx";
import { Percent } from "@/components/l10n/Percent.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { PayInvoiceButton } from "@/modules/billing/ActionButtons.tsx";
import { ReportStatus } from "@/modules/billing/ReportStatus.tsx";
import { useBillingAccount } from "@/modules/billing/billingAccountQuery.ts";
import { baInvoiceQueryAtom } from "@/modules/billing/billingInvoicesQuery.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { baUsageQueryAtom } from "@/modules/billing/billingUsageQuery.ts";
import { DownloadPdf } from "@/modules/billing/invoice/DownloadPdf.tsx";
import { InvoiceDueDate, InvoiceIssueDate, InvoicePeriod } from "@/modules/billing/invoice/InvoiceDates.tsx";
import { VatFieldTitle } from "@/modules/billing/invoice/VatFieldTitle.tsx";
import {
    getInvoiceDiscountAmount,
    getInvoiceDiscountPercentage,
    getRecordDiscountAmount,
    getRecordDiscountPercentage,
    getRecordLineTotal,
    hasAnyInvoiceDiscount,
    hasAppliedRecordDiscount,
} from "@/modules/billing/invoice/invoiceDiscounts.ts";
import {
    createCurrentUsage,
    getIsCurrentUsage,
    getIsReport,
    getPayableInvoiceId,
    hasPdfDownload,
    invoiceName,
    reportIdTitle,
} from "@/modules/billing/invoice/invoiceLogic.tsx";
import { billingAccountLink } from "@/modules/billing/links.ts";
import { qtyUnitLabels } from "@/modules/l10n/units.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { BillingAccountInvoice, InvoiceId, InvoiceRecord } from "@warrenio/api-spec/spec.oats.gen";
import { filterFalse } from "@warrenio/utils/collections/filterFalse";
import { getAndAssert } from "@warrenio/utils/collections/getAndAssert";
import { getOwn } from "@warrenio/utils/collections/getOwnProperty";
import type { ReactNode } from "react";

export function InvoiceView({ baId, invoiceId }: { baId: number; invoiceId: InvoiceId }) {
    //#region Hooks
    const invoicesData = useSuspenseQueryAtom(baInvoiceQueryAtom(baId));
    const usageData = useSuspenseQueryAtom(baUsageQueryAtom(baId));
    const eBillingAccount = useBillingAccount(baId);
    //#endregion

    const invoice =
        invoiceId === "current"
            ? createCurrentUsage([...usageData.values()], eBillingAccount.account)
            : getAndAssert(invoicesData, invoiceId, "Invoice");

    return (
        <>
            <InvoiceToolbar invoice={invoice} eBillingAccount={eBillingAccount} />
            <InvoiceDetails invoice={invoice} accountCell={eBillingAccount.account.title} />
            <Separator />
            <ServicesContent invoice={invoice} />
        </>
    );
}

interface InvoiceToolbarProps {
    invoice: BillingAccountInvoice;
    eBillingAccount: EBillingAccount;
}

function InvoiceToolbar({ invoice, eBillingAccount }: InvoiceToolbarProps) {
    const payableInvoiceId = getPayableInvoiceId(invoice, eBillingAccount);
    return (
        <Toolbar>
            <WButton
                color="muted"
                size="bar"
                variant="ghost"
                action={billingAccountLink(eBillingAccount.account)}
                icon="jp-icon-caretleft"
            >
                <Trans>Billing account</Trans>
            </WButton>

            {payableInvoiceId != null && (
                <PayInvoiceButton account={eBillingAccount} invoice_id={payableInvoiceId}>
                    <Trans>Pay now</Trans>
                </PayInvoiceButton>
            )}

            {hasPdfDownload(invoice) && <DownloadPdf item={invoice} />}
        </Toolbar>
    );
}

interface InvoiceDetailsProps {
    invoice: BillingAccountInvoice;
    /** Value cell of the "Billing account" row */
    accountCell: ReactNode;
    /** Extra rows, rendered after "Document No." */
    children?: ReactNode;
}

export function InvoiceDetails({ invoice, accountCell, children }: InvoiceDetailsProps) {
    const { t } = useLingui();
    const { status, name, type, document_type } = invoice;

    const isReport = getIsReport(invoice);
    const isCurrentUsage = getIsCurrentUsage(invoice);
    const showIssueDate = !isReport && !isCurrentUsage;
    const showDueDate = showIssueDate && document_type !== "credit_note";

    const id = reportIdTitle(invoice);
    return (
        <ContentPane className="VStack gap-4">
            <ViewTitle title={invoiceName(invoice, "long")} />

            <DetailsHolder>
                <DetailsTable>
                    {!!id && (
                        <DetailsTableRow title={t`Document No.`}>
                            <ClipBoardTooltip>{id}</ClipBoardTooltip>
                        </DetailsTableRow>
                    )}

                    {children}

                    {showIssueDate && (
                        <DetailsTableRow title={t`Issue date`}>
                            {name && type !== "credit_topup" ? "-" : <InvoiceIssueDate invoice={invoice} />}
                        </DetailsTableRow>
                    )}

                    {showDueDate && (
                        <DetailsTableRow title={t`Due date`}>
                            {name && type !== "credit_topup" ? "-" : <InvoiceDueDate invoice={invoice} />}
                        </DetailsTableRow>
                    )}

                    {!isCurrentUsage && type !== "credit_topup" && (
                        <DetailsTableRow title={t`Period`}>
                            <InvoicePeriod invoice={invoice} />
                        </DetailsTableRow>
                    )}

                    {!isReport && (
                        <DetailsTableRow title={t`Status`}>
                            <ReportStatus value={status} />
                        </DetailsTableRow>
                    )}

                    <DetailsTableRow title={t`Billing account`}>{accountCell}</DetailsTableRow>
                </DetailsTable>

                <Spacer />

                <DetailsTable>
                    {getSummaryFields(invoice).map(({ title, value }, index) => (
                        <DetailsTableRow key={index} title={title}>
                            {value}
                        </DetailsTableRow>
                    ))}
                </DetailsTable>
            </DetailsHolder>
        </ContentPane>
    );
}

interface SummaryField {
    title: ReactNode;
    value: ReactNode;
}

function getSummaryFields(invoice: BillingAccountInvoice): SummaryField[] {
    const { totals } = invoice;

    const isReport = getIsReport(invoice);
    const hasDiscount = hasAnyInvoiceDiscount(invoice);

    const subtotal: SummaryField = {
        title:
            invoice.has_record_level_discount && hasDiscount ? (
                <Trans>Subtotal before discounts</Trans>
            ) : (
                <Trans>Subtotal</Trans>
            ),
        value: <CurrencyBalance value={totals.subtotal} />,
    };

    const discount: SummaryField | undefined = hasDiscount
        ? {
              title: <DiscountTitle percentage={getInvoiceDiscountPercentage(invoice)} />,
              value: <CurrencyBalance value={getInvoiceDiscountAmount(invoice)} />,
          }
        : undefined;

    if (isReport) {
        return filterFalse<SummaryField>([subtotal, discount]);
    }

    return filterFalse<SummaryField>([
        subtotal,
        discount,
        !!totals.credit && {
            title: <Trans>Credit</Trans>,
            value: <CurrencyBalance value={totals.credit} />,
        },
        {
            title: <TotalExclVat />,
            value: <CurrencyBalance value={totals.total_before_tax} />,
        },
        {
            title: <VatFieldTitle invoice={invoice} />,
            value: <CurrencyBalance value={totals.vat_tax} />,
        },
        {
            title: <Trans>Total</Trans>,
            value: <CurrencyBalance value={totals.total_before_tax + totals.vat_tax} />,
        },
    ]);
}

function DiscountTitle({ percentage }: { percentage?: number }) {
    if (percentage == null) {
        return <Trans>Discount</Trans>;
    }

    return (
        <>
            <Trans>Discount</Trans> <Percent value={percentage} />
        </>
    );
}

export function TotalExclVat() {
    const vatSymbol = <VatText />;
    return <Trans comment="Short of: Total excluding">Total excl. {vatSymbol}</Trans>;
}

export function ExclVat() {
    const vatSymbol = <VatText />;
    return <Trans comment="Short of: Excluding (VAT/GST etc.)">Excl. {vatSymbol}</Trans>;
}

function Unit({ value }: { value: string }) {
    return getOwn(qtyUnitLabels, value) ?? value;
}

function RecordDiscountValue({ item }: { item: InvoiceRecord }) {
    if (!hasAppliedRecordDiscount(item)) {
        return "-";
    }

    const percentage = getRecordDiscountPercentage(item);

    return (
        <>
            {percentage != null && (
                <>
                    <Percent value={percentage} />{" "}
                </>
            )}
            <CurrencyBalance value={getRecordDiscountAmount(item)} />
        </>
    );
}

export function ServicesContent({ invoice }: { invoice: BillingAccountInvoice }) {
    const { records_list } = invoice;
    const showRecordLevelDiscounts = invoice.has_record_level_discount && records_list.some(hasAppliedRecordDiscount);

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Services</Trans>} />

            <WTable>
                <thead>
                    <tr>
                        <th>
                            <Trans>Service</Trans>
                        </th>
                        <th>
                            <Trans comment="Short of: Quantity">Qty.</Trans>
                        </th>
                        <th className="text-right">
                            <Trans>Unit</Trans>
                        </th>
                        <th className="text-right">
                            <Trans>Amount</Trans>
                        </th>
                        {showRecordLevelDiscounts && (
                            <>
                                <th className="text-right">
                                    <Trans comment="Column title for invoice records' discount">Discount</Trans>
                                </th>
                                <th className="text-right">
                                    <Trans comment="Column title for invoice records' totals - record amount subtracted by discount">
                                        Line total
                                    </Trans>
                                </th>
                            </>
                        )}
                    </tr>
                </thead>
                <WTableBody>
                    {records_list.map((item) => {
                        const { id, item_price, name, qty, qty_unit, amount } = item;

                        return (
                            <tr key={id}>
                                <td>{name}</td>
                                <td>
                                    {qty} <Unit value={qty_unit} />
                                </td>
                                <td className="text-right">
                                    <CurrencyHourly value={item_price} /> / <Unit value={qty_unit} />
                                </td>
                                <td className="text-right">
                                    <CurrencyBalance value={amount} />
                                </td>
                                {showRecordLevelDiscounts && (
                                    <>
                                        <td className="text-right">
                                            <RecordDiscountValue item={item} />
                                        </td>
                                        <td className="text-right">
                                            <CurrencyBalance value={getRecordLineTotal(item)} />
                                        </td>
                                    </>
                                )}
                            </tr>
                        );
                    })}
                </WTableBody>
                <tfoot className={TC.Clean}>
                    {getSummaryFields(invoice).map(({ title, value }, index) => (
                        <tr key={index}>
                            <td colSpan={showRecordLevelDiscounts ? 4 : 2} />
                            <td className="text-right">{title}</td>
                            <td className="text-right">{value}</td>
                        </tr>
                    ))}
                </tfoot>
            </WTable>
        </ContentPane>
    );
}
